import React, { useState } from 'react';
import { Box, Typography, Card, CardContent, Grid, Button, Avatar, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemButton, ListItemText, Divider } from '@mui/material';
import { useApp } from '../context/AppContext';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import StoreIcon from '@mui/icons-material/Store';

export default function OwnerAppLauncherView() {
    const { setActiveView, subscribedModules, currentUser, setCurrentUser } = useApp();
    const [showBranchDialog, setShowBranchDialog] = useState(false);
    const [targetApp, setTargetApp] = useState(null);

    // Check if Biz Analytics is allowed
    const hasBizAccess = Array.isArray(subscribedModules) ? subscribedModules.includes('bizAnalytics') : true; 
    // Defaulting to true if subscribedModules is undefined, adjust as needed

    const handleAppClick = (appName) => {
        if (currentUser?.is_multi_branch && currentUser?.branches && currentUser.branches.length > 1) {
            setTargetApp(appName);
            setShowBranchDialog(true);
        } else {
            proceedToApp(appName);
        }
    };

    const proceedToApp = (appName, branchId = null, branchName = null) => {
        if (branchId) {
            // Update the context and sessionStorage with selected branch
            const updatedUser = { ...currentUser, branch_id: branchId, branch_name: branchName };
            setCurrentUser(updatedUser);
            sessionStorage.setItem('react_pos_current_user', JSON.stringify(updatedUser));
            
            // Dispatch custom event to tell AppContext to refresh data for new branch
            window.dispatchEvent(new CustomEvent('dinexa_branch_changed', { detail: { branch_id: branchId } }));
        }

        setShowBranchDialog(false);

        if (appName === 'pos') {
            localStorage.removeItem('dinexa_app_mode');
            if (currentUser?.allowed_menus && Array.isArray(currentUser.allowed_menus)) {
                if (currentUser.allowed_menus.includes('tables')) {
                    setActiveView('tables');
                } else if (currentUser.allowed_menus.includes('quickPos')) {
                    setActiveView('quickPos');
                } else {
                    setActiveView('tables');
                }
            } else {
                setActiveView('tables');
            }
        } else if (appName === 'biz') {
            localStorage.setItem('dinexa_app_mode', 'biz');
            setActiveView('biz');
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9', p: 3 }}>
            <Box sx={{ width: '100%', maxWidth: 500 }}>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', textAlign: 'center', mb: 1 }}>
                    Welcome Owner!
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748b', textAlign: 'center', mb: 4 }}>
                    Select an application to launch
                </Typography>

                <Grid container spacing={3} direction="column">
                    <Grid item xs={12}>
                        <Card 
                            onClick={() => handleAppClick('pos')}
                            sx={{ 
                                borderRadius: 4, 
                                cursor: 'pointer', 
                                border: '2px solid transparent',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 12px 24px -10px rgba(0, 0, 0, 0.15)'
                                }
                            }}
                        >
                            <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 64, height: 64, mr: 3 }}>
                                    <StorefrontIcon fontSize="large" />
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        DinexaPOS
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                        Point of Sale, Billing & Floor Management
                                    </Typography>
                                </Box>
                                <ArrowForwardIosIcon color="action" />
                            </CardContent>
                        </Card>
                    </Grid>

                    {hasBizAccess && (
                        <Grid item xs={12}>
                            <Card 
                                onClick={() => handleAppClick('biz')}
                                sx={{ 
                                    borderRadius: 4, 
                                    cursor: 'pointer', 
                                    border: '2px solid transparent',
                                    background: 'linear-gradient(to right bottom, #ffffff, #f8fafc)',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        borderColor: '#0284c7',
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 24px -10px rgba(2, 132, 199, 0.15)'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center' }}>
                                    <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0284c7', width: 64, height: 64, mr: 3 }}>
                                        <TrendingUpIcon fontSize="large" />
                                    </Avatar>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                            DinexaBiz
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            Owner Analytics, Reports & Insights
                                        </Typography>
                                    </Box>
                                    <ArrowForwardIosIcon color="action" />
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>

                {!hasBizAccess && (
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 4, color: '#94a3b8' }}>
                        DinexaBiz analytics is not enabled for your subscription plan.
                    </Typography>
                )}
            </Box>

            <Dialog open={showBranchDialog} onClose={() => setShowBranchDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 800, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#0f172a' }}>
                    Select Branch / Outlet
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <List sx={{ pt: 0, pb: 0 }}>
                        {currentUser?.branches?.map((branch, index) => (
                            <React.Fragment key={branch.id}>
                                <ListItem disablePadding>
                                    <ListItemButton onClick={() => proceedToApp(targetApp, branch.id, branch.name)} sx={{ p: 2, '&:hover': { bgcolor: '#f0fdf4' } }}>
                                        <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0284c7', mr: 2 }}>
                                            <StoreIcon />
                                        </Avatar>
                                        <ListItemText 
                                            primary={<Typography sx={{ fontWeight: 700, color: '#1e293b' }}>{branch.name}</Typography>} 
                                            secondary={<Typography variant="body2" sx={{ color: '#64748b' }}>{branch.address || 'No address provided'}</Typography>} 
                                        />
                                        <ArrowForwardIosIcon sx={{ fontSize: 16, color: '#cbd5e1' }} />
                                    </ListItemButton>
                                </ListItem>
                                {index < currentUser.branches.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </DialogContent>
            </Dialog>
        </Box>
    );
}
